import { SocketService } from '@events/chat/socket.service';
import { EventEmitter, Injectable, Injector } from '@angular/core';
import { UserHelperService } from '@helpers/user-helper.service';
import { UserAttributesI } from '@interfaces/user.interface';
import { Socket } from '@node_modules/socket.io-client/build/esm';
import { firstValueFrom } from 'rxjs';
import { ReportDownloadHistoryService } from '@modules/reports/report-download-history/report-download-history.service';
import { DownloadHistoryStatusE } from '@enums/dowload-history.enum';
import { TranslateService } from '@ngx-translate/core';
import { AclAbilityService } from '@services/acl/acl.ability';
import { ResourceNames } from '@enums/acl/resources.enum';
import { ToastrService } from 'ngx-toastr';
@Injectable({
  providedIn: 'root',
})
export class FileExporterSocketService {
  private socket!: Socket;
  private eventNameProcess = 'process-export-file-creation';
  private buffers: { position: number; buffer: string; length: number; jobId: string }[] = [];
  public processingFiles$ = new EventEmitter<{
    fileName?: string;
    complete?: boolean;
    arrayBuffer?: ArrayBuffer;
    jobId: string;
    fail?: boolean;
    url?: string;
    date: Date;
    loadOnly?: boolean;
    forceShow?: boolean;
  } | null>();

  // Resolved on demand, never during APP_INITIALIZER: ngx-toastr's Overlay captures ApplicationRef in its
  // constructor, so creating ToastrService while the app still boots freezes an unusable ref and breaks
  // every toast in the session (the Overlay is a root singleton shared by the whole app).
  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  constructor(
    private socketService: SocketService,
    private userHelper: UserHelperService,
    private reportDownloadHistoryService: ReportDownloadHistoryService,
    private translateService: TranslateService,
    private aclService: AclAbilityService,
    private injector: Injector
  ) {}

  public init() {
    this.socket = this.socketService.init();
    const canList = this.aclService.canListAll(ResourceNames.REPORTS__DOWNLOAD_HISTORY);
    canList && this.loadFromDatabase();

    if (this.socket) {
      this.socket.on(this.eventNameProcess, (data) => {
        const row = JSON.parse(data);
        const { jobId, fail } = row;
        if (fail) {
          this.handleFail(jobId);
          return;
        }
        this.buffers.push(row);
        const jobBuffer = this.buffers.filter((item) => item.jobId === jobId);
        const end = jobBuffer.length == row.length;
        if (end) {
          const base64String = jobBuffer
            .sort((a, b) => (a.position > b.position ? 1 : a.position < b.position ? -1 : 0))
            .map((row) => row.buffer)
            .join('');
          const { timeoutHandle, fileName } = this.processingMap.get(jobId);
          try {
            const arrayBuffer = this.base64ToArrayBuffer(base64String);
            fileName &&
              this.processingFiles$.emit({
                fileName,
                complete: true,
                jobId,
                arrayBuffer,
                date: new Date(),
              });
            try {
              const message = this.translateService
                .instant('REPORT_PROCESSING_FINISH')
                .replace('${NAME}', this.translateService.instant(fileName));
              this.toastr.success(message);
            } catch (error) {
              console.warn('export finish: toastr unavailable', error);
            }
          } finally {
            clearTimeout(timeoutHandle);
            this.socket.emit('leave-rooms', [jobId]);
            this.buffers = this.buffers.filter((row) => row.jobId !== jobId);
            this.processingMap.delete(jobId);
          }
        }
      });
    }
  }

  private async loadFromDatabase() {
    const user = this.userHelper.getUserAttributes() as UserAttributesI;
    const filter = {
      status: [DownloadHistoryStatusE.SUCCESS, DownloadHistoryStatusE.IN_PROGRESS].join(','),
      users: user['custom:userId'],
    };
    const { data } = await firstValueFrom(
      this.reportDownloadHistoryService.getReportData<any>('desc', 1, 5, 'createdAt', filter)
    );

    if (data.length) {
      for (const row of data) {
        const fileName = this.translateService.instant(row.name) + '.' + row.format;
        const date = new Date(row.createdAt);
        if (row.status === DownloadHistoryStatusE.SUCCESS) {
          this.processingFiles$.emit({ fileName, jobId: row.jobId, date, loadOnly: true });
          this.processingMap.set(row.jobId, { fileName });
          setTimeout(() => {
            this.processingFiles$.emit({
              fileName,
              complete: true,
              jobId: row.jobId,
              date,
              url: row.url,
              loadOnly: true,
            });
          }, 1000);
        }
        if (row.status === DownloadHistoryStatusE.IN_PROGRESS) {
          this.loadBuffer(row.jobId, fileName, false, date, true);
        }
      }
    }
  }

  private handleFail(jobId: string) {
    if (this.processingMap.has(jobId)) {
      const { fileName } = this.processingMap.get(jobId);
      this.processingMap.delete(jobId);
      try {
        const message = this.translateService.instant('REPORT_PROCESSING_FAIL');
        this.toastr.error(message.replace('${NAME}', fileName));
      } catch (error) {
        console.warn('export fail: toastr unavailable', error);
      }
    }
    this.buffers = this.buffers.filter((item) => item.jobId !== jobId);
    this.processingFiles$.emit({
      fail: true,
      date: new Date(),
      jobId,
    });
  }

  private processingMap = new Map<string, any>();

  loadBuffer(
    jobId: string,
    fileName?: string,
    showToasters: boolean = true,
    date = new Date(),
    forceShow: boolean = false,
    timeout = 60 * 5 * 1000
  ) {
    // join the job room before any UI feedback — a toastr failure must never
    // prevent the buffer subscription (the export would silently never arrive)
    const timeoutHandle = setTimeout(() => {
      this.socket.emit('leave-rooms', [jobId]);
    }, timeout);
    this.processingMap.set(jobId, { timeoutHandle, fileName });
    this.socket.emit('join-rooms', [jobId]);

    fileName && this.processingFiles$.emit({ fileName, jobId, date, forceShow });
    if (showToasters) {
      try {
        let message = this.translateService.instant('REPORT_PROCESSING');
        message = message.replace('${NAME}', this.translateService.instant(fileName!));
        this.toastr.info(message);
      } catch (error) {
        console.warn('loadBuffer: toastr unavailable', error);
      }
    }
  }

  private base64ToArrayBuffer(base64: string) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
